import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import API, { PUBLIC_URL } from '../utils/const';
import {
    lastCheckedSession,
    setLastCheckedSession,
    setSessionExpiredHandler,
    User,
    whoami,
} from '../utils/session';
import { VerifySession } from '../utils/session';
import { trackAuthIdentify } from '../utils/analytics';


interface AuthContextType {
    isAuthenticated: boolean | null;
    user: User | null;
    getLoggedUser: () => Promise<User | null>;
    setRedirectOnUnauthenticated: (redirect: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);


export function AuthProvider({ children }: { children: ReactNode }) {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const [redirectOnUnauthenticated, setRedirectOnUnauthenticated] = useState<boolean>(false);


    // Resolves the current session into auth state. TOS is the only step that
    // must redirect from here; "unauthenticated" is just state, and the single
    // redirect site below decides whether to send the user to the login page.
    const getLoggedUser: () => Promise<User | null> = async () => {
        const result = await whoami();
        if (result === VerifySession.TOS) {
            window.location.href = `${PUBLIC_URL}/tos?logged=true`;
            return null;
        }
        if (result === VerifySession.MISSING || result === VerifySession.EXPIRED) {
            setIsAuthenticated(false);
            setUser(null);
            return null;
        }
        const loggedInUser = result as User;
        setIsAuthenticated(true);
        setUser(loggedInUser);
        setLastCheckedSession(new Date());
        trackAuthIdentify(loggedInUser);
        return loggedInUser;
    }

    useEffect(() => {
        setSessionExpiredHandler(() => {
            setIsAuthenticated(false);
            setUser(null);
        });
        return () => setSessionExpiredHandler(null);
    }, []);

    useEffect(() => {
        if (user === null) {
            getLoggedUser();
        }
    }, [user]);

    useEffect(() => {
        if (isAuthenticated === false && redirectOnUnauthenticated) {
            const pathname = window.location.pathname;
            if (!pathname.includes("/login") && !pathname.includes("/book")) {
                window.location.href = `${PUBLIC_URL}/login?login=true&msg=${btoa("Please login")}`
            }
        }
    }, [isAuthenticated, redirectOnUnauthenticated]);

    // Add window focus event listener to check auth status when user returns to the tab
    useEffect(() => {
        const handleFocus = () => {
            const pathname = window.location.pathname;
            // Only check if we're not already on the login page or booking page
            if (pathname.includes("/login") || pathname.includes("/book")) {
                return
            }
            // Only check every 2 minutes
            if (lastCheckedSession() && lastCheckedSession() > new Date(Date.now() - 1000 * 60 * 2)) {
                return
            }
            getLoggedUser();
        };

        window.addEventListener('focus', handleFocus);

        // Clean up the event listener on unmount
        return () => {
            window.removeEventListener('focus', handleFocus);
        };
    }, []);

    return (
        <AuthContext.Provider value={{ isAuthenticated, user, getLoggedUser, setRedirectOnUnauthenticated }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export function useOptionalAuth(): AuthContextType | undefined {
    return useContext(AuthContext);
}